import React from "react";
import { Box } from "@mui/joy";
import DarkTypography from "components/common/DarkTypography";
import GradientTooltipCard from "components/common/GradientTooltipCard";
import { ExcursionTemplateLimitsChips } from "./ExcursionCountWithMaxChip";
import type {
  BookingStatus,
  Excursion,
  ExcursionChipDropPending,
  GroupLabels,
  ExcursionTemplateLimitsItem,
} from "api/excursions/Excursion.typed";
import { ExcursionStatusCancelled, ExcursionStatusPending } from "api/excursions/Excursion.typed";
import {
  colorTokens,
  excursionTooltipCardInteractiveHoverSx,
  excursionTooltipParamLabelColor,
  excursionTooltipSublineColor,
  textTokens,
} from "styles/globalStyles";
import { getExcursionStatusEmoji } from "../../../excursions/shared/utils/planningUtils";
import {
  buildExcursionTemplateLimitChipModel,
  excursionTimeRangeLabel,
} from "../../../excursions/planning/display/excursionCardUtils";
import { excursionTooltipExcursionParamEntries } from "./excursionTooltipHelpers";
import ExcursionTooltipGroupsBody from "./ExcursionTooltipGroupsBody";
import ExcursionTooltipParamEntry from "./ExcursionTooltipParamEntry";

export type ExcursionTooltipFloaterContentProps = {
  excursion: Excursion;
  canEdit: boolean;
  groupLabels?: GroupLabels[];
  makeGroupChipDropHandlers?: (targetExcursionId: number, targetGroupIndex: number) => object;
  excursionTemplatesList?: ExcursionTemplateLimitsItem[];
  bookingStatusMap?: Record<number, BookingStatus>;
  getCustomerInProgressTrainingsTooltip?: (customerId: number) => string | null;
  closeTooltipAfterBookingAction: () => void;
  onClientDragStart?: (sourceExcursionId: number) => void;
  onClientDragEnd?: () => void;
  clientChipDragSourceEnabled?: boolean;
  reconnectDropSurfacePointerEventsInsideFloater?: boolean;
  /** Opens full excursion modal (planning) or external action. */
  onGradientCardNavigate?: (e: React.MouseEvent) => void;
  /** Tooltip bridge; omit in drawer. */
  tooltipContentId?: string;
  onTooltipContentMouseEnter?: () => void;
  onTooltipContentMouseLeave?: (e: React.MouseEvent) => void;
  /** Default: region (drawer). Tooltip passes dialog|tooltip. */
  contentRole?: "dialog" | "tooltip" | "region";
  ariaModal?: boolean;
  chipDropPending?: ExcursionChipDropPending | null;
};

export default function ExcursionTooltipFloaterContent({
  excursion,
  canEdit,
  groupLabels,
  makeGroupChipDropHandlers,
  excursionTemplatesList,
  bookingStatusMap,
  getCustomerInProgressTrainingsTooltip,
  closeTooltipAfterBookingAction,
  onClientDragStart,
  onClientDragEnd,
  clientChipDragSourceEnabled = true,
  reconnectDropSurfacePointerEventsInsideFloater = false,
  onGradientCardNavigate,
  tooltipContentId,
  onTooltipContentMouseEnter,
  onTooltipContentMouseLeave,
  contentRole = "region",
  ariaModal,
  chipDropPending = null,
}: ExcursionTooltipFloaterContentProps) {
  const timeStr = excursionTimeRangeLabel(excursion);
  const cancellationMotif =
    excursion.status === ExcursionStatusCancelled && excursion.cancellation_reason?.trim()
      ? excursion.cancellation_reason.trim()
      : "";
  const groups = excursion.groups ?? [];
  const canEditTooltip =
    canEdit && (excursion.status ?? ExcursionStatusPending) === ExcursionStatusPending;
  const groupDropHandlers =
    canEditTooltip && makeGroupChipDropHandlers ? makeGroupChipDropHandlers : undefined;

  const {
    totalPeople,
    groupCount,
    maxPeople: maxPeopleExcursion,
    maxGroups: maxGroupsExcursion,
  } = buildExcursionTemplateLimitChipModel(excursion, excursionTemplatesList);
  const showExcursionLimits = true;

  return (
    <Box
      id={tooltipContentId}
      data-excursion-tooltip
      role={contentRole === "region" ? undefined : contentRole}
      aria-modal={contentRole !== "region" ? ariaModal : undefined}
      onMouseEnter={onTooltipContentMouseEnter}
      onMouseLeave={onTooltipContentMouseLeave}
      sx={{ display: "inline-block", minWidth: 0 }}
    >
      <GradientTooltipCard
        data-excursion-tooltip
        onClick={
          onGradientCardNavigate
            ? (e: React.MouseEvent) => {
                e.stopPropagation();
                closeTooltipAfterBookingAction();
                onGradientCardNavigate(e);
              }
            : undefined
        }
        role={onGradientCardNavigate ? "button" : undefined}
        tabIndex={onGradientCardNavigate ? 0 : undefined}
        onKeyDown={
          onGradientCardNavigate
            ? (e: React.KeyboardEvent) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  closeTooltipAfterBookingAction();
                  onGradientCardNavigate(e as unknown as React.MouseEvent);
                }
              }
            : undefined
        }
        sx={onGradientCardNavigate ? excursionTooltipCardInteractiveHoverSx : undefined}
        header={
          <>
            <DarkTypography
              level="body-md"
              sx={{ fontWeight: 700, color: textTokens.color.primary, minWidth: 0 }}
            >
              {getExcursionStatusEmoji(excursion.status)} {excursion.name}
            </DarkTypography>
            <DarkTypography level="body-xs" sx={{ color: excursionTooltipSublineColor, mt: 0.25 }}>
              {timeStr}
            </DarkTypography>
            {cancellationMotif !== "" && (
              <DarkTypography
                level="body-xs"
                sx={{
                  color: excursionTooltipSublineColor,
                  mt: 0.35,
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                }}
              >
                {cancellationMotif}
              </DarkTypography>
            )}
            {(() => {
              const entries = excursionTooltipExcursionParamEntries(
                excursion,
                excursionTemplatesList,
              );
              return (
                <Box sx={{ mt: 0.75, display: "flex", flexDirection: "column", gap: 0.25 }}>
                  {entries.length > 0
                    ? entries.map(([key, v]) => (
                        <ExcursionTooltipParamEntry
                          key={key}
                          label={key}
                          value={v}
                          color={excursionTooltipParamLabelColor}
                        />
                      ))
                    : null}
                </Box>
              );
            })()}
          </>
        }
        footer={
          showExcursionLimits ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                flexWrap: "nowrap",
                gap: 0.5,
                mt: 1,
                pt: 1,
                borderTop: `1px solid ${colorTokens.border.default}`,
              }}
            >
              <ExcursionTemplateLimitsChips
                totalPeople={totalPeople}
                groupCount={groupCount}
                maxPeople={maxPeopleExcursion}
                maxGroups={maxGroupsExcursion}
                size="sm"
              />
            </Box>
          ) : null
        }
      >
        <ExcursionTooltipGroupsBody
          excursion={excursion}
          groups={groups}
          groupLabels={groupLabels}
          groupDropHandlers={groupDropHandlers}
          canEdit={canEditTooltip}
          bookingStatusMap={bookingStatusMap}
          getCustomerInProgressTrainingsTooltip={getCustomerInProgressTrainingsTooltip}
          onClientDragStart={onClientDragStart}
          onClientDragEnd={onClientDragEnd}
          clientChipDragSourceEnabled={clientChipDragSourceEnabled}
          reconnectDropSurfacePointerEventsInsideFloater={
            reconnectDropSurfacePointerEventsInsideFloater
          }
          chipDropPending={chipDropPending}
        />
      </GradientTooltipCard>
    </Box>
  );
}
